import {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {trpc, createMutationErrorHandler} from '@common-web/lib/trpc';
import {Card, CardContent} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import {AlertDescription} from '@common-web/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@common-web/components/ui/alert-dialog';
import {
  Upload,
  CheckCircle,
  AlertCircle,
  ListChecks,
  Check,
  RefreshCw,
  Link2,
  Info,
  X,
  Trash2,
  History,
  ArrowRight,
  ChevronRight,
  TableProperties,
  Plus,
  AlertTriangle,
} from 'lucide-react';
import {TransformationEditorModal} from './components/TransformationEditor';
import {DeductionCombineModal} from './components/DeductionCombineModal';
import {DeductionPivotModal} from './components/DeductionPivotModal';
import {
  PayrollFileColumnMapping,
  PayrollAnchorField,
  PayrollFileColumnTransformationColumn,
  PayrollFileColumnTransformation,
} from '@api/lib/payrollProcessing/types';
import {PayrollTargetField} from '@api/lib/payrollProcessing/constants';
import {
  addPreviewToAdvancedChange,
  applySingleColumnMapping,
  applySuggestionsToMappings,
  applyDeductionsMapping,
} from './utils/transformationUtils';
import {validateConfiguration, LocalValidationError} from './utils/validationUtils';
import {toast} from '@common-web/hooks/use-toast';
import {
  ExtractedColumn,
  ExtractedFile,
  FileForUpload,
  FileIdValidation,
  Mapping,
  MappingSuggestion,
  MappingViewStyle,
  PivotColumn,
  PivotConfig,
  TargetFieldInfo,
} from '@client/pages/payroll/types';
import {ColumnExtractUploader} from '@client/pages/payroll/components/FileUploadDropzone';
import {AnchorFieldsEditor} from '@client/pages/payroll/components/AnchorFieldsEditor';
import {
  ConfigurationSidebar,
  ConfigurationStep,
  StepCompletionStatus,
} from '@client/pages/payroll/components/ConfigurationSidebar';
import clsx from 'clsx';
import {formatFilesForProcessing, guessFileType} from '@client/pages/payroll/utils/fileUtils';
import {fileToBase64} from '@common-web/lib/fileToBase64';
import {ColumnSelectorCard} from '@client/pages/payroll/components/ColumnSelectorCard';
import {ScrollArea} from '@common-web/components/ui/scroll-area';
import {FileIdBadge} from '@client/pages/payroll/components/FileIdBadge';
import {FileIdValidationAlert} from '@client/pages/payroll/components/FileIdValidationAlert';
import {
  applyFileIdSwaps,
  compareFileIds,
  PrunedMappingInfo,
  RemovedMappingInfo,
  removeInvalidColumnsFromAnchors,
  removeInvalidColumnsFromMapping,
} from '@client/pages/payroll/utils/columnValidation';
import {
  buildSuggestions,
  getBestColumnMappingSuggestion,
} from '@client/pages/payroll/utils/suggestionUtils';
import {AutoRemovedMappingAlert} from '@client/pages/payroll/components/AutoRemovedMappingAlert';

export function PayrollConfiguration({
  agentId,
  config,
  requiredFields,
  onConfigurationComplete,
  files,
  onFilesChange,
}: {
  agentId: string;
  config?: {
    columnMappings?: PayrollFileColumnMapping[] | null;
    anchorFields?: PayrollAnchorField[] | null;
    ignoredColumns?: string[] | null;
    pivotConfigs?: PivotConfig[] | null;
  };
  requiredFields: {targetFields: TargetFieldInfo[]} | undefined;
  onConfigurationComplete: () => Promise<void>;
  files: FileForUpload[];
  onFilesChange: (files: FileForUpload[]) => void;
  isConfigured: boolean;
}) {
  const [activeStep, setActiveStep] = useState<ConfigurationStep>('upload');
  const [extracting, setExtracting] = useState(false);
  const [extractedFiles, setExtractedFiles] = useState<ExtractedFile[]>([]);
  const [mappings, setMappings] = useState<PayrollFileColumnMapping[]>(
    () => config?.columnMappings || [],
  );
  const [anchorFields, setAnchorFields] = useState<PayrollAnchorField[]>(
    () => config?.anchorFields || [],
  );
  const primaryFileId = useMemo(() => {
    const fromFiles = files.find((f) => f.isPrimary)?.fileId;
    if (fromFiles) return fromFiles;
    return anchorFields.find((a) => !!a.primaryFileId)?.primaryFileId;
  }, [files, anchorFields]);

  const handlePrimaryFileIdChange = useCallback(
    (id: string | undefined) => {
      const updatedFiles = files.map((f) => ({
        ...f,
        isPrimary: f.fileId === id,
      }));
      onFilesChange(updatedFiles);
    },
    [files, onFilesChange],
  );

  const [ignoredColumns, setIgnoredColumns] = useState<string[]>(
    () => config?.ignoredColumns || [],
  );
  const [removedMappingsAlert, setRemovedMappingsAlert] = useState<{
    show: boolean;
    removedMappings: RemovedMappingInfo[];
    prunedMappings: PrunedMappingInfo[];
  }>({
    show: false,
    removedMappings: [],
    prunedMappings: [],
  });
  const [fileIdValidation, setFileIdValidation] = useState<FileIdValidation>({
    show: false,
    missingFileIds: [],
    currentFileIds: [],
    previousFileIds: [],
    mode: undefined,
    suspectedSwappedPairs: [],
  });
  const [editingField, setEditingField] = useState<PayrollTargetField | null>(null);
  const [openModals, setOpenModals] = useState({
    deductionsModal: false,
    advancedModal: false,
    pivotModal: false,
  });
  const [pivotConfigs, setPivotConfigs] = useState<PivotConfig[]>(() => config?.pivotConfigs || []);
  const [pivotColumns, setPivotColumns] = useState<Record<string, PivotColumn[]>>({});
  const [mappingsViewStyle, setMappingsViewStyle] = useState<MappingViewStyle>('list');
  const [suggestions, setSuggestions] = useState<Record<string, MappingSuggestion | undefined>>({});
  const [validationErrors, setValidationErrors] = useState<LocalValidationError[]>([]);
  const [unmappedColumns, setUnmappedColumns] = useState<string[]>([]);
  const [focusedError, setFocusedError] = useState<LocalValidationError | null>(null);
  const [showSaveSuccessModal, setShowSaveSuccessModal] = useState(false);
  const hasTriggeredCompletionRef = useRef(false);
  const reExtractAfterSwapRef = useRef(false);
  const mappingFieldRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const extractFileColumns = trpc.group.payrollProcessing.extractFileColumns.useMutation({
    onError: createMutationErrorHandler('Failed to extract columns'),
  });
  const extractPivotKeyValues = trpc.group.payrollProcessing.extractPivotKeyValues.useMutation();
  const updateColumnMappings = trpc.group.payrollProcessing.updateColumnMappings.useMutation({
    onError: createMutationErrorHandler('Failed to save configuration'),
  });

  const targetFields = useMemo(() => requiredFields?.targetFields || [], [requiredFields]);

  const getTargetFieldLabel = useCallback(
    (field: PayrollTargetField) => {
      const targetField = targetFields.find((tf) => tf.field === field);
      return targetField ? targetField.label : field.replace(/_/g, ' ').toLowerCase();
    },
    [targetFields],
  );

  useEffect(() => {
    if (config?.columnMappings) {
      setMappings(config.columnMappings);
    }
    if (config?.anchorFields) {
      setAnchorFields(config.anchorFields);
    }
    if (config?.ignoredColumns) {
      setIgnoredColumns(config.ignoredColumns);
    }
    if (config?.pivotConfigs) {
      setPivotConfigs(config.pivotConfigs);
    }
  }, [config]);

  useEffect(() => {
    if (targetFields.length > 0) {
      const validation = validateConfiguration(
        targetFields,
        mappings,
        extractedFiles,
        anchorFields,
        ignoredColumns,
      );
      setValidationErrors(validation.structuredErrors);
      setUnmappedColumns((prev) => {
        const newUnmapped = validation.unmappedColumns;
        if (prev.length !== newUnmapped.length || prev.some((col) => !newUnmapped.includes(col))) {
          return newUnmapped;
        }
        return prev;
      });
      setIgnoredColumns((prev) => {
        const usedColumns = validation.usedColumns;
        const nextIgnored = prev.filter((colId) => !usedColumns.includes(colId));
        if (nextIgnored.length !== prev.length) {
          return nextIgnored;
        }
        return prev;
      });

      setFocusedError((prev) => {
        if (!prev) return null;
        const stillExists = validation.structuredErrors.some((err) => {
          if (err.type !== prev.type) return false;
          if (err.type === 'mapping') return err.targetField === prev.targetField;
          if (err.type === 'anchor') return err.anchorFieldIdx === prev.anchorFieldIdx;
          return true;
        });
        return stillExists ? prev : null;
      });
    }
  }, [mappings, anchorFields, ignoredColumns, targetFields, extractedFiles]);

  useEffect(() => {
    if (primaryFileId && anchorFields.length > 0) {
      const needsUpdate = anchorFields.some((a) => a.primaryFileId !== primaryFileId);
      if (needsUpdate) {
        setAnchorFields((prev) => prev.map((a) => ({...a, primaryFileId})));
      }
    }
  }, [primaryFileId, anchorFields]);

  const handleStepChange = useCallback((step: ConfigurationStep) => {
    setActiveStep(step);
    setFocusedError(null);
  }, []);

  const handleFilesSelected = useCallback(
    async (selectedFiles: FileForUpload[]) => {
      onFilesChange(selectedFiles);
      setExtractedFiles([]);
    },
    [onFilesChange],
  );

  const handleExtract = useCallback(async () => {
    if (files.length === 0) return;
    setExtracting(true);
    try {
      const payloadFiles = await formatFilesForProcessing(files);
      const response = await extractFileColumns.mutateAsync({agentId, files: payloadFiles});

      let extractedFileResults: ExtractedFile[] = response.results.map((r) => ({
        file: {fileId: r.file.fileId, fileName: r.file.fileName},
        columns: r.columns,
      }));

      // Regenerate pivot columns from saved pivot configs
      if (pivotConfigs.length > 0) {
        const newPivotColumns: Record<string, PivotColumn[]> = {};

        for (const pivotConfig of pivotConfigs) {
          const fileData = files.find((f) => f.fileId === pivotConfig.fileId);
          const extractedFile = extractedFileResults.find(
            (e) => e.file.fileId === pivotConfig.fileId,
          );

          if (fileData && extractedFile) {
            try {
              const keyValuesResult = await extractPivotKeyValues.mutateAsync({
                agentId,
                files: [
                  {
                    fileId: pivotConfig.fileId,
                    fileName: fileData.file.name,
                    fileContent: await fileToBase64(fileData.file),
                    fileType: guessFileType(fileData.file.name),
                    hasHeaders: fileData.hasHeaders,
                    delimiter: fileData.delimiter,
                  },
                ],
                fileId: pivotConfig.fileId,
                keyColumnName: pivotConfig.keyColumn.name,
              });

              const generatedPivotColumns: PivotColumn[] = keyValuesResult.values.map(
                (keyValue, idx) => ({
                  name: pivotConfig.labelPrefix
                    ? `${pivotConfig.labelPrefix}${keyValue}`
                    : keyValue,
                  index: -1000 - idx,
                  samples: [],
                  pivotKeyValue: keyValue,
                  isPivotColumn: true as const,
                  fileId: pivotConfig.fileId,
                }),
              );

              newPivotColumns[pivotConfig.fileId] = generatedPivotColumns;
            } catch (error) {
              console.error(
                'Failed to regenerate pivot columns for file:',
                pivotConfig.fileId,
                error,
              );
            }
          }
        }

        setPivotColumns(newPivotColumns);

        extractedFileResults = extractedFileResults.map((extractedFile) => ({
          ...extractedFile,
          pivotColumns: newPivotColumns[extractedFile.file.fileId],
        }));
      }

      setExtractedFiles(extractedFileResults);

      buildSuggestions(targetFields, extractedFileResults, setSuggestions, primaryFileId);

      const fileIdValidation = compareFileIds(mappings, {
        type: 'extracted',
        files: extractedFileResults,
      });
      if (fileIdValidation.missingFileIds.length > 0) {
        setFileIdValidation({
          show: true,
          missingFileIds: fileIdValidation.missingFileIds,
          currentFileIds: fileIdValidation.currentFileIds,
          previousFileIds: fileIdValidation.previousFileIds,
          mode: 'missing',
          suspectedSwappedPairs: [],
        });
      } else if (
        fileIdValidation.suspectedSwappedPairs &&
        fileIdValidation.suspectedSwappedPairs.length > 0
      ) {
        setFileIdValidation({
          show: true,
          missingFileIds: [],
          currentFileIds: fileIdValidation.currentFileIds,
          previousFileIds: fileIdValidation.previousFileIds,
          mode: 'swapped',
          suspectedSwappedPairs: fileIdValidation.suspectedSwappedPairs,
        });
      } else {
        setFileIdValidation({
          show: false,
          missingFileIds: [],
          currentFileIds: [],
          previousFileIds: [],
          mode: undefined,
          suspectedSwappedPairs: [],
        });
        const {removed, pruned, retained} = removeInvalidColumnsFromMapping(
          mappings,
          extractedFileResults,
        );
        setAnchorFields((prev) => removeInvalidColumnsFromAnchors(prev, extractedFileResults));
        if (removed.length > 0 || pruned.length > 0) {
          setRemovedMappingsAlert({
            show: true,
            removedMappings: removed,
            prunedMappings: pruned,
          });
          setMappings(retained);
        } else {
          setRemovedMappingsAlert({
            show: false,
            removedMappings: [],
            prunedMappings: [],
          });
        }

        // Auto-navigate to column mappings after extraction
        setActiveStep('mapping');
      }
    } finally {
      setExtracting(false);
    }
  }, [
    files,
    agentId,
    targetFields,
    extractFileColumns,
    extractPivotKeyValues,
    mappings,
    primaryFileId,
    pivotConfigs,
  ]);

  const onCloseMissingFileIdAlert = useCallback(() => {
    setFileIdValidation({
      show: false,
      missingFileIds: [],
      currentFileIds: [],
      previousFileIds: [],
      mode: undefined,
      suspectedSwappedPairs: [],
    });
    setRemovedMappingsAlert({
      show: false,
      removedMappings: [],
      prunedMappings: [],
    });
    if (!reExtractAfterSwapRef.current) {
      const {removed, pruned, retained} = removeInvalidColumnsFromMapping(mappings, extractedFiles);
      if (removed.length > 0 || pruned.length > 0) {
        setMappings(retained);
      }
      setAnchorFields((prev) => removeInvalidColumnsFromAnchors(prev, extractedFiles));
    }
  }, [mappings, extractedFiles]);

  const applySwappedFileIds = useCallback(() => {
    const pairs = fileIdValidation.suspectedSwappedPairs || [];
    if (!pairs || pairs.length === 0) return;
    const next = applyFileIdSwaps(files, pairs);
    reExtractAfterSwapRef.current = true;
    onFilesChange(next);
    onCloseMissingFileIdAlert();
  }, [files, onFilesChange, fileIdValidation.suspectedSwappedPairs, onCloseMissingFileIdAlert]);

  useEffect(() => {
    if (reExtractAfterSwapRef.current) {
      reExtractAfterSwapRef.current = false;
      handleExtract();
    }
  }, [files, handleExtract]);

  const columnsByFileId = useMemo(() => {
    const map: Record<string, ExtractedColumn[]> = {};
    extractedFiles.forEach((f) => {
      const allColumns = [...f.columns];
      if (f.pivotColumns) {
        allColumns.push(
          ...f.pivotColumns.map((pc) => ({
            name: pc.name,
            index: pc.index,
            samples: pc.samples,
            pivotKeyValue: pc.pivotKeyValue,
          })),
        );
      }
      map[f.file.fileId] = allColumns;
    });
    return map;
  }, [extractedFiles]);

  const combinedColumns = useMemo(() => {
    return extractedFiles.flatMap((f) => {
      const regularCols = f.columns.map((c) => ({...c, fileId: f.file.fileId}));
      const pivotCols = (f.pivotColumns || []).map((c) => ({
        name: c.name,
        index: c.index,
        samples: c.samples,
        fileId: f.file.fileId,
        isPivotColumn: true,
        pivotKeyValue: c.pivotKeyValue,
      }));
      return [...regularCols, ...pivotCols];
    });
  }, [extractedFiles]);

  const onChangeSingleColumn = useCallback(
    (targetField: PayrollTargetField, fileId: string, index: number) => {
      applySingleColumnMapping(
        {targetField, fileId, index},
        columnsByFileId,
        setMappings,
        setSuggestions,
      );
    },
    [columnsByFileId],
  );

  const removeColumnMapping = useCallback(
    (targetField: PayrollTargetField) => {
      setMappings((prev) => prev.filter((m) => m.targetField !== targetField));

      const bestSuggestion = getBestColumnMappingSuggestion(
        targetField,
        extractedFiles,
        primaryFileId,
      );

      if (bestSuggestion) {
        setSuggestions((prev) => ({
          ...prev,
          [targetField]: bestSuggestion,
        }));
      }
    },
    [extractedFiles],
  );

  const acceptAllSuggestions = useCallback(() => {
    if (!extractedFiles.length) return;
    const updatedMappings = applySuggestionsToMappings(
      columnsByFileId,
      targetFields,
      mappings,
      suggestions,
    );
    setMappings(updatedMappings);
    setSuggestions({});
  }, [columnsByFileId, targetFields, mappings, suggestions]);

  const handleErrorClick = useCallback((error: LocalValidationError) => {
    setFocusedError(error);

    switch (error.type) {
      case 'mapping': {
        setActiveStep('mapping');
        if (error.targetField) {
          setTimeout(() => {
            const el = mappingFieldRefs.current[error.targetField!];
            if (el) {
              el.scrollIntoView({behavior: 'smooth', block: 'center'});
            }
          }, 100);
        }
        break;
      }
      case 'anchor': {
        setActiveStep('linking');
        break;
      }
      case 'unmapped': {
        setActiveStep('unmapped');
        break;
      }
      default:
        break;
    }
  }, []);

  const saveConfiguration = async () => {
    const validation = validateConfiguration(
      targetFields,
      mappings,
      extractedFiles,
      anchorFields,
      ignoredColumns,
    );

    if (!validation.isValid) {
      toast({
        title: 'Configuration Validation Error',
        description: `Please fix the following issues before saving:\n\n${validation.errors.join('\n')}`,
        variant: 'destructive',
      });
      return;
    }

    try {
      await updateColumnMappings.mutateAsync({
        agentId,
        columnMappings: mappings,
        anchorFields,
        ignoredColumns,
        pivotConfigs: pivotConfigs.length > 0 ? pivotConfigs : undefined,
      });
      hasTriggeredCompletionRef.current = false;
      setShowSaveSuccessModal(true);
    } catch (error) {
      console.error('Failed to save configuration:', error);
      toast({
        title: 'Failed to save configuration:',
        description: `Failed to save configuration. Please try again or contact support if the problem persists.`,
        variant: 'destructive',
      });
    }
  };

  const onSaveSuccessModalOpenChange = useCallback(
    (open: boolean) => {
      setShowSaveSuccessModal(open);
      if (!open && !hasTriggeredCompletionRef.current) {
        hasTriggeredCompletionRef.current = true;
        onConfigurationComplete();
      }
    },
    [onConfigurationComplete],
  );

  const resetConfiguration = useCallback(() => {
    setMappings([]);
    setAnchorFields([]);
    setIgnoredColumns([]);
    setExtractedFiles([]);
    setSuggestions({});
    setEditingField(null);
    onFilesChange([]);
    setFocusedError(null);
    setActiveStep('upload');

    toast({
      title: 'Configuration Cleared',
      description:
        'Local configuration has been cleared. Click "Save Configuration" to make this permanent.',
    });
  }, [onFilesChange]);

  const restoreToSaved = useCallback(() => {
    setMappings(config?.columnMappings || []);
    setAnchorFields(config?.anchorFields || []);
    setIgnoredColumns(config?.ignoredColumns || []);
    setExtractedFiles([]);
    setSuggestions({});
    setEditingField(null);
    onFilesChange([]);
    setActiveStep('upload');

    toast({
      title: 'Configuration Restored',
      description:
        'Your changes have been discarded and the last saved configuration has been restored.',
    });
  }, [config, onFilesChange]);

  const handleAdvancedChange = useCallback(
    (newMapping: Mapping) => {
      if (!editingField) return;
      const updatedMappings = addPreviewToAdvancedChange(
        mappings,
        newMapping,
        editingField,
        extractedFiles,
      );
      setMappings(updatedMappings);
    },
    [mappings, editingField, extractedFiles],
  );

  const handleDeductionsMapping = useCallback(
    (columns: PayrollFileColumnTransformationColumn[]) => {
      if (!editingField) return;
      const updatedMappings = applyDeductionsMapping(
        mappings,
        columns,
        editingField,
        extractedFiles,
      );
      setMappings(updatedMappings);
    },
    [mappings, editingField, extractedFiles],
  );

  useEffect(() => {
    if (
      files.length > 0 &&
      extractedFiles.length === 0 &&
      !extracting &&
      agentId &&
      !extractFileColumns.isPending
    ) {
      handleExtract();
    }
  }, []);

  const onIgnoreUnmappedColumn = useCallback(
    (action: {type: 'all'} | {type: 'single'; columnId: string}) => {
      if (action.type === 'all') {
        setIgnoredColumns((prev) => {
          return Array.from(new Set([...prev, ...unmappedColumns]));
        });
        toast({
          title: 'Columns Ignored',
          description: `${unmappedColumns.length} column${unmappedColumns.length !== 1 ? 's' : ''} will be ignored during processing.`,
        });
      } else {
        const [, columnName] = action.columnId.split(':');
        setIgnoredColumns((prev) => [...prev, action.columnId]);
        toast({
          title: 'Column Ignored',
          description: `Column "${columnName}" will be ignored during processing.`,
        });
      }
    },
    [unmappedColumns],
  );

  const handleOpenAdvancedModal = useCallback(
    (mode: 'general' | 'deductions', targetField: PayrollTargetField) => {
      setEditingField(targetField);
      setOpenModals((prev) => ({
        ...prev,
        advancedModal: mode === 'general',
        deductionsModal: mode === 'deductions',
      }));
    },
    [],
  );

  const handleCloseAdvancedModals = useCallback(() => {
    setEditingField(null);
    setOpenModals((prev) => ({...prev, advancedModal: false, deductionsModal: false}));
  }, []);

  const handleOpenPivotModal = useCallback(() => {
    setOpenModals((prev) => ({...prev, pivotModal: true}));
  }, []);

  const handleClosePivotModal = useCallback(() => {
    setOpenModals((prev) => ({...prev, pivotModal: false}));
  }, []);

  const handlePivotConfigConfirm = useCallback(
    (config: PivotConfig | null, generatedColumns: PivotColumn[]) => {
      if (!config) {
        setPivotConfigs([]);
        setPivotColumns({});
        setExtractedFiles((prev) => prev.map((f) => ({...f, pivotColumns: undefined})));
      } else {
        setPivotConfigs((prev) => {
          const existingIdx = prev.findIndex((p) => p.fileId === config.fileId);
          if (existingIdx >= 0) {
            const updated = [...prev];
            updated[existingIdx] = config;
            return updated;
          }
          return [...prev, config];
        });

        setPivotColumns((prev) => ({
          ...prev,
          [config.fileId]: generatedColumns,
        }));

        setExtractedFiles((prev) =>
          prev.map((f) =>
            f.file.fileId === config.fileId ? {...f, pivotColumns: generatedColumns} : f,
          ),
        );

        toast({
          title: 'Pivot Configuration Saved',
          description: `${generatedColumns.length} column${generatedColumns.length !== 1 ? 's' : ''} generated from deduction codes.`,
        });
      }
    },
    [],
  );

  const completionStatus: StepCompletionStatus = useMemo(() => {
    const filesExtracted = extractedFiles.length > 0;
    const allRequiredMapped =
      filesExtracted && mappings.length > 0 && validationErrors.length === 0;

    return {
      upload: filesExtracted,
      mapping: filesExtracted && allRequiredMapped,
      linking: filesExtracted && (anchorFields.length > 0 || files.length <= 1),
      unmapped:
        extractedFiles.length === 0 ? 'hidden' : unmappedColumns.length > 0 ? 'warning' : 'ok',
      ignored: ignoredColumns.length > 0 ? 'info' : 'hidden',
      pivot:
        extractedFiles.length === 0
          ? 'hidden'
          : pivotConfigs.length > 0
            ? 'configured'
            : 'available',
    };
  }, [
    extractedFiles.length,
    mappings.length,
    validationErrors.length,
    anchorFields.length,
    files.length,
    unmappedColumns.length,
    ignoredColumns.length,
    pivotConfigs.length,
  ]);

  return (
    <div className='flex gap-3 -mx-4 -mt-3'>
      <ConfigurationSidebar
        activeStep={activeStep}
        onStepChange={handleStepChange}
        completionStatus={completionStatus}
        unmappedCount={unmappedColumns.length}
        ignoredCount={ignoredColumns.length}
        validationErrors={validationErrors}
        mappedCount={mappings.length}
        totalFields={targetFields.length}
        filesExtracted={extractedFiles.length > 0}
        onSaveConfiguration={saveConfiguration}
        isSaving={updateColumnMappings.isPending}
      />
      <div className='flex-1 min-w-0'>
        {!!config && !!config.columnMappings && config.columnMappings.length > 0 && (
          <div className='bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-6'>
            <div className='px-4 py-3 bg-white flex items-center justify-between gap-4 flex-wrap'>
              <div className='flex items-center gap-3'>
                <div className='p-2 bg-internal-brand-blue/20 rounded-lg'>
                  <History className='h-5 w-5 text-internal-brand-blue' />
                </div>
                <div>
                  <h3 className='text-sm font-semibold text-internal-brand-navy'>
                    Saved Configuration Loaded
                  </h3>
                  <p className='text-xs text-internal-brand-navy'>
                    Modify it, restore to saved, or clear to start fresh
                  </p>
                </div>
              </div>
              <div className='flex items-center gap-2'>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={restoreToSaved}
                  className='font-medium text-internal-brand-navy hover:bg-internal-brand-navy/10 border-internal-brand-navy hover:border-internal-brand-navy/50'
                >
                  <RefreshCw className='h-4 w-4 mr-2' />
                  Restore Saved
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant='outline'
                      size='sm'
                      className='font-medium text-internal-brand-navy hover:bg-internal-brand-navy/10 border-internal-brand-navy hover:border-internal-brand-navy/50'
                    >
                      <Trash2 className='h-4 w-4 mr-2' />
                      Clear All
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Clear Configuration?</AlertDialogTitle>
                    </AlertDialogHeader>
                    <AlertDialogDescription>
                      This will clear all column mappings and file linking settings from your
                      current working state.
                      <br />
                      <br />
                      <strong>Note:</strong> This only clears your local changes. You'll need to
                      click "Save Configuration" to make this permanent. You can click "Restore
                      Saved" at any time to undo this action.
                    </AlertDialogDescription>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={resetConfiguration}
                        className='bg-red-600 hover:bg-red-700 text-white'
                      >
                        Clear Configuration
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>
        )}
        <div className='bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden'>
          {activeStep === 'upload' && (
            <UploadStepContent
              files={files}
              onFilesChange={handleFilesSelected}
              extracting={extracting}
              onExtract={handleExtract}
              primaryFileId={primaryFileId}
              onPrimaryFileIdChange={handlePrimaryFileIdChange}
            />
          )}
          {activeStep === 'mapping' && (
            <MappingStepContent
              targetFields={targetFields}
              mappings={mappings}
              combinedColumns={combinedColumns}
              extractedFiles={extractedFiles}
              onChangeSingleColumn={onChangeSingleColumn}
              removeColumnMapping={removeColumnMapping}
              mappingsViewStyle={mappingsViewStyle}
              suggestions={suggestions}
              onOpenAdvanced={handleOpenAdvancedModal}
              validationErrors={validationErrors}
              focusedError={focusedError}
              acceptAllSuggestions={acceptAllSuggestions}
              mappingFieldRefs={mappingFieldRefs}
            />
          )}
          {activeStep === 'linking' && (
            <LinkingStepContent
              extractedFiles={extractedFiles}
              anchorFields={anchorFields}
              onChange={setAnchorFields}
              primaryFileId={primaryFileId}
              onPrimaryFileIdChange={handlePrimaryFileIdChange}
              files={files}
            />
          )}
          {activeStep === 'unmapped' && (
            <UnmappedStepContent
              unmappedColumns={unmappedColumns}
              onIgnoreUnmappedColumn={onIgnoreUnmappedColumn}
              extractedFiles={extractedFiles}
            />
          )}
          {activeStep === 'ignored' && (
            <IgnoredStepContent
              ignoredColumns={ignoredColumns}
              setIgnoredColumns={setIgnoredColumns}
            />
          )}
          {activeStep === 'pivot' && (
            <PivotStepContent
              pivotConfigs={pivotConfigs}
              pivotColumns={pivotColumns}
              extractedFiles={extractedFiles}
              onOpenPivotModal={handleOpenPivotModal}
            />
          )}
          {activeStep === 'issues' && (
            <IssuesStepContent
              validationErrors={validationErrors}
              onErrorClick={handleErrorClick}
            />
          )}
        </div>
      </div>
      <TransformationEditorModal
        open={openModals.advancedModal}
        onClose={handleCloseAdvancedModals}
        editingField={editingField}
        targetFieldLabel={editingField ? getTargetFieldLabel(editingField) : ''}
        extracted={extractedFiles}
        mapping={mappings.find((m) => m.targetField === editingField)}
        onChange={handleAdvancedChange}
      />
      <DeductionCombineModal
        open={openModals.deductionsModal}
        onClose={handleCloseAdvancedModals}
        targetFieldLabel={editingField ? getTargetFieldLabel(editingField) : ''}
        extracted={extractedFiles}
        initialColumns={
          editingField
            ? (
                mappings.find(
                  (m) =>
                    m.targetField === editingField &&
                    m.columnTransformation.type === 'COLUMN_ADDITION',
                )?.columnTransformation as Extract<
                  PayrollFileColumnTransformation,
                  {type: 'COLUMN_ADDITION'}
                >
              )?.columns
            : undefined
        }
        onConfirm={handleDeductionsMapping}
      />
      <DeductionPivotModal
        open={openModals.pivotModal}
        onClose={handleClosePivotModal}
        extractedFiles={extractedFiles}
        anchorFields={anchorFields}
        existingPivotConfig={pivotConfigs[0]}
        onConfirm={handlePivotConfigConfirm}
        files={files}
        agentId={agentId}
      />
      <AlertDialog open={showSaveSuccessModal} onOpenChange={onSaveSuccessModalOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Configuration saved!</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>
            If your file format ever changes, click on the{' '}
            <span className={'font-medium'}>Configuration</span> button at the top of this page to
            make changes.
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogAction
              className='bg-internal-brand-blue hover:bg-internal-brand-blue-dark text-white'
              onClick={() => {
                onSaveSuccessModalOpenChange(false);
              }}
            >
              Validate Files
              <ArrowRight className={'w-5 h-5 ml-2'} />
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AutoRemovedMappingAlert
        removedMappingsAlert={removedMappingsAlert}
        setRemovedMappingsAlert={setRemovedMappingsAlert}
        getTargetFieldLabel={getTargetFieldLabel}
      />
      <FileIdValidationAlert
        fileIdValidation={fileIdValidation}
        onClose={onCloseMissingFileIdAlert}
        onSwapFileIds={applySwappedFileIds}
      />
    </div>
  );
}

const UploadStepContent = memo(
  ({
    files,
    onFilesChange,
    extracting,
    onExtract,
    primaryFileId,
    onPrimaryFileIdChange,
  }: {
    files: FileForUpload[];
    onFilesChange: (files: FileForUpload[]) => void;
    extracting: boolean;
    onExtract: () => void;
    primaryFileId?: string;
    onPrimaryFileIdChange?: (fileId: string | undefined) => void;
  }) => {
    return (
      <>
        <div className='px-6 py-4 bg-internal-brand-navy border-b'>
          <div className='flex items-center gap-3'>
            <div className='p-2 bg-white/20 rounded-lg'>
              <Upload className='h-5 w-5 text-white' />
            </div>
            <div>
              <h2 className='text-lg font-semibold text-white'>Upload Payroll Files</h2>
              <p className='text-sm text-white/80'>
                Add one or more source files to map. Click "Extract Columns" to read headers and
                sample data.
              </p>
            </div>
          </div>
        </div>
        <div className='p-6'>
          <ColumnExtractUploader
            isExtracting={extracting}
            onExtract={onExtract}
            files={files}
            onFilesUpdated={onFilesChange}
            primaryFileId={primaryFileId}
            onPrimaryFileIdChange={onPrimaryFileIdChange}
          />
        </div>
      </>
    );
  },
);

const MappingStepContent = memo(
  ({
    targetFields,
    mappings,
    combinedColumns,
    extractedFiles,
    onChangeSingleColumn,
    removeColumnMapping,
    mappingsViewStyle,
    suggestions,
    onOpenAdvanced,
    validationErrors,
    focusedError,
    acceptAllSuggestions,
    mappingFieldRefs,
  }: {
    targetFields: TargetFieldInfo[];
    mappings: PayrollFileColumnMapping[];
    combinedColumns: ExtractedColumn[];
    extractedFiles: ExtractedFile[];
    onChangeSingleColumn: (targetField: PayrollTargetField, fileId: string, index: number) => void;
    removeColumnMapping: (targetField: PayrollTargetField) => void;
    mappingsViewStyle: MappingViewStyle;
    suggestions: Record<string, MappingSuggestion | undefined>;
    onOpenAdvanced: (mode: 'deductions' | 'general', targetField: PayrollTargetField) => void;
    validationErrors: LocalValidationError[];
    focusedError: LocalValidationError | null;
    acceptAllSuggestions: () => void;
    mappingFieldRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  }) => {
    return (
      <>
        <div className='px-6 py-4 bg-internal-brand-navy border-b'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-3'>
              <div className='p-2 bg-white/20 rounded-lg'>
                <ListChecks className='h-5 w-5 text-white' />
              </div>
              <div>
                <h2 className='text-lg font-semibold text-white'>Column Mappings</h2>
                <p className='text-sm text-white/80'>
                  Map each required field to a column from your file(s).
                </p>
              </div>
            </div>
            <Badge className='bg-white/10 text-white border-white/20 font-semibold'>
              {mappings.length} of {targetFields.length} mapped
            </Badge>
          </div>
        </div>
        <div className='p-4'>
          {targetFields.length === 0 ? (
            <div className='py-8 text-center text-muted-foreground'>Loading target fields...</div>
          ) : extractedFiles.length === 0 && mappings.length === 0 ? (
            <div className='py-12 text-center'>
              <Upload className='h-12 w-12 mx-auto text-gray-300 mb-4' />
              <div className='text-lg font-medium text-gray-600 mb-2'>No files extracted yet</div>
              <p className='text-sm text-gray-500'>
                Upload and extract your payroll files first to see available columns.
              </p>
            </div>
          ) : (
            <div className='space-y-4'>
              {extractedFiles.length === 0 && mappings.length > 0 && (
                <div className='flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg'>
                  <Info className='h-5 w-5 text-blue-600 shrink-0' />
                  <div className='text-sm text-blue-800'>
                    <span className='font-medium'>Read-only view.</span> Upload and extract your
                    payroll files to edit these column mappings.
                  </div>
                </div>
              )}
              {Object.keys(suggestions).length > 0 && (
                <div className='flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg'>
                  <span className='text-sm text-yellow-800'>
                    We found {Object.keys(suggestions).length} suggested mapping
                    {Object.keys(suggestions).length !== 1 ? 's' : ''} based on column names.
                  </span>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={acceptAllSuggestions}
                    className='border-yellow-400 text-yellow-700 hover:border-yellow-400 hover:bg-yellow-100'
                  >
                    <Check className='h-4 w-4 mr-1' /> Apply All Suggestions
                  </Button>
                </div>
              )}
              <ScrollArea orientation={'horizontal'}>
                <div>
                  {targetFields.map((targetField) => {
                    const error = validationErrors.find((e) => e.targetField === targetField.field);
                    const isFocused =
                      !!error &&
                      !!focusedError &&
                      focusedError.type === 'mapping' &&
                      focusedError.targetField === targetField.field;

                    return (
                      <div
                        key={targetField.field}
                        ref={(el) => {
                          mappingFieldRefs.current[targetField.field] = el;
                        }}
                      >
                        <ColumnSelectorCard
                          id={`card-mapping-${targetField.field}`}
                          targetField={targetField}
                          mapping={mappings.find((m) => m.targetField === targetField.field)}
                          combinedColumns={combinedColumns}
                          extractedFiles={extractedFiles}
                          onChangeSingleColumn={onChangeSingleColumn}
                          removeColumnMapping={removeColumnMapping}
                          viewStyle={mappingsViewStyle}
                          suggestion={suggestions[targetField.field]}
                          onOpenAdvanced={onOpenAdvanced}
                          error={isFocused ? error?.message : undefined}
                        />
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>
          )}
        </div>
      </>
    );
  },
);

const LinkingStepContent = memo(
  ({
    extractedFiles,
    anchorFields,
    onChange,
    primaryFileId,
    onPrimaryFileIdChange,
    files,
  }: {
    extractedFiles: ExtractedFile[];
    anchorFields: PayrollAnchorField[];
    onChange: (val: PayrollAnchorField[]) => void;
    primaryFileId?: string;
    onPrimaryFileIdChange?: (fileId: string | undefined) => void;
    files: FileForUpload[];
  }) => {
    return (
      <>
        <div className='px-6 py-4 bg-internal-brand-navy border-b'>
          <div className='flex items-center gap-3'>
            <div className='p-2 bg-white/20 rounded-lg'>
              <Link2 className='h-5 w-5 text-white' />
            </div>
            <div>
              <h2 className='text-lg font-semibold text-white'>
                File Linking {files.length <= 1 ? '(Optional)' : ''}
              </h2>
              <p className='text-sm text-white/80'>
                Set up how to match records across multiple files.
              </p>
            </div>
          </div>
        </div>
        <div className='p-4'>
          <AnchorFieldsEditor
            extractedFiles={extractedFiles}
            anchorFields={anchorFields}
            onChange={onChange}
            primaryFileId={primaryFileId}
            onPrimaryFileIdChange={onPrimaryFileIdChange}
          />
        </div>
      </>
    );
  },
);

const UnmappedStepContent = memo(
  ({
    unmappedColumns,
    onIgnoreUnmappedColumn,
    extractedFiles,
  }: {
    unmappedColumns: string[];
    onIgnoreUnmappedColumn: (action: {type: 'all'} | {type: 'single'; columnId: string}) => void;
    extractedFiles: ExtractedFile[];
  }) => {
    return (
      <>
        <div className='px-6 py-4 bg-amber border-b'>
          <div className='flex items-center gap-3'>
            <div className='p-2 bg-white/20 rounded-lg'>
              <AlertCircle className='h-5 w-5 text-white' />
            </div>
            <div>
              <h2 className='text-lg font-semibold text-white'>Unmapped Columns</h2>
              <p className='text-sm text-white/80'>
                These columns are not mapped to any field. Map or ignore them.
              </p>
            </div>
          </div>
        </div>
        <div className='p-6'>
          {unmappedColumns.length === 0 ? (
            <div className='py-12 text-center'>
              <CheckCircle className='h-12 w-12 mx-auto text-emerald mb-4' />
              <div className='text-lg font-medium text-gray-600 mb-2'>All columns mapped!</div>
              <p className='text-sm text-gray-500'>
                Every column from your files has been mapped or ignored.
              </p>
            </div>
          ) : (
            <>
              <div className='mb-4 p-3 bg-amber/5 rounded-lg border border-amber/20 flex items-center justify-between'>
                <p className='text-sm text-gray-600 flex-1'>
                  The following column{unmappedColumns.length !== 1 ? 's' : ''} from your uploaded
                  file{extractedFiles.length !== 1 ? 's' : ''} are not mapped. You must either map{' '}
                  {unmappedColumns.length !== 1 ? 'them' : 'it'} or click "Ignore" to explicitly
                  mark {unmappedColumns.length !== 1 ? 'them' : 'it'} as unused.
                </p>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant='outline'
                      size='sm'
                      className='border-amber text-amber hover:bg-amber hover:border-amber hover:text-white ml-4'
                    >
                      Ignore All
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Ignore all unmapped columns?</AlertDialogTitle>
                    </AlertDialogHeader>
                    <AlertDialogDescription>
                      This will add all currently unmapped columns to the Ignored Columns list so
                      they are excluded during processing.
                    </AlertDialogDescription>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => onIgnoreUnmappedColumn({type: 'all'})}
                        className='bg-amber hover:bg-amber text-white'
                      >
                        Ignore All
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
              <div className='grid grid-cols-1 lg:grid-cols-2 gap-3'>
                {unmappedColumns.map((columnId) => {
                  const [fileId, columnName] = columnId.split(':');
                  const file = extractedFiles.find((f) => f.file.fileId === fileId);
                  const column = file?.columns.find((c) => c.name === columnName);

                  return (
                    <Card key={columnId} className='border-amber/30 shadow-sm'>
                      <CardContent className='p-4'>
                        <div className='flex items-center justify-between gap-3'>
                          <div className='flex-1 min-w-0'>
                            <FileIdBadge className={'mb-1'} fileId={fileId} />
                            <div
                              className='font-medium text-internal-brand-navy truncate'
                              title={columnName}
                            >
                              {columnName}
                            </div>
                            {column?.samples?.[0] && (
                              <div
                                className='text-xs text-internal-brand-gray mt-1 truncate'
                                title={column.samples[0]}
                              >
                                e.g. {column.samples[0]}
                              </div>
                            )}
                          </div>
                          <Button
                            variant='outline'
                            size='sm'
                            onClick={() => onIgnoreUnmappedColumn({type: 'single', columnId})}
                            className='border-amber text-amber hover:bg-amber hover:text-white shrink-0'
                          >
                            Ignore
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </>
    );
  },
);

const IgnoredStepContent = memo(
  ({
    ignoredColumns,
    setIgnoredColumns,
  }: {
    ignoredColumns: string[];
    setIgnoredColumns: (cols: string[]) => void;
  }) => {
    return (
      <>
        <div className='px-6 py-4 bg-gray-500 border-b'>
          <div className='flex items-center gap-3'>
            <div className='p-2 bg-white/20 rounded-lg'>
              <Info className='h-5 w-5 text-white' />
            </div>
            <div>
              <h2 className='text-lg font-semibold text-white'>Ignored Columns</h2>
              <p className='text-sm text-white/80'>
                These columns will not be used during payroll processing.
              </p>
            </div>
          </div>
        </div>
        <div className='p-6'>
          {ignoredColumns.length === 0 ? (
            <div className='py-12 text-center'>
              <Info className='h-12 w-12 mx-auto text-gray-300 mb-4' />
              <div className='text-lg font-medium text-gray-600 mb-2'>No ignored columns</div>
              <p className='text-sm text-gray-500'>
                Columns you choose to ignore will appear here.
              </p>
            </div>
          ) : (
            <>
              <div className='mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 flex items-center justify-between'>
                <div className='text-sm text-gray-600 flex-1'>
                  These columns will not be used during payroll processing.
                </div>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => setIgnoredColumns([])}
                  className='shrink-0'
                >
                  Remove all
                </Button>
              </div>
              <div className='flex flex-wrap gap-2'>
                {ignoredColumns.map((colId) => {
                  const [fileId, columnName] = colId.split(':');
                  return (
                    <div
                      key={colId}
                      className='flex items-center gap-2 pr-3 pl-1 py-1 rounded-full text-xs font-medium bg-gray-100 text-black border border-gray-200 hover:bg-gray-200'
                    >
                      <FileIdBadge fileId={fileId} />
                      <span className='text-sm text-gray-800 font-medium'>{columnName}</span>
                      <button
                        onClick={() => {
                          setIgnoredColumns(ignoredColumns.filter((c) => c !== colId));
                        }}
                        className='ml-1 hover:text-red-600'
                      >
                        <X className='h-3 w-3' />
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </>
    );
  },
);

const PivotStepContent = memo(
  ({
    pivotConfigs,
    pivotColumns,
    extractedFiles,
    onOpenPivotModal,
  }: {
    pivotConfigs: PivotConfig[];
    pivotColumns: Record<string, PivotColumn[]>;
    extractedFiles: ExtractedFile[];
    onOpenPivotModal: () => void;
  }) => {
    const hasExtractedFiles = extractedFiles.length > 0;

    return (
      <>
        <div className='px-6 py-4 bg-internal-brand-navy border-b'>
          <div className='flex items-center gap-3'>
            <div className='p-2 bg-white/20 rounded-lg'>
              <TableProperties className='h-5 w-5 text-white' />
            </div>
            <div>
              <h2 className='text-lg font-semibold text-white'>Deduction Code Pivot</h2>
              <p className='text-sm text-white/80'>
                Convert rows with deduction codes into individual columns.
              </p>
            </div>
          </div>
        </div>
        <div className='p-6'>
          <div className='flex items-start gap-3 mb-6 p-4 bg-emerald/5 rounded-lg border border-emerald/20'>
            <div className='p-2 bg-emerald/20 rounded-lg'>
              <TableProperties className='h-5 w-5 text-emerald' />
            </div>
            <div className='flex-1'>
              <div className='text-sm text-gray-600'>
                If your file has multiple rows per employee with deduction codes and amounts, you
                can automatically convert them into individual columns for each unique code.
              </div>
            </div>
          </div>
          {pivotConfigs.length > 0 ? (
            <div className='space-y-3'>
              {pivotConfigs.map((pivotConfig, idx) => {
                const generatedColumnCount = pivotColumns[pivotConfig.fileId]?.length;
                return (
                  <div
                    key={idx}
                    className='p-4 bg-white border border-emerald/30 rounded-lg flex items-center justify-between'
                  >
                    <div className='flex items-center gap-3'>
                      <FileIdBadge fileId={pivotConfig.fileId} />
                      <div>
                        <div className='text-sm font-medium text-internal-brand-navy'>
                          {pivotConfig.keyColumn.name}
                          {generatedColumnCount !== undefined
                            ? ` → ${generatedColumnCount} columns`
                            : ''}
                          {pivotConfig.labelPrefix && (
                            <span className='text-gray-500 font-normal ml-1'>
                              (prefix: "{pivotConfig.labelPrefix}")
                            </span>
                          )}
                        </div>
                        <div className='text-xs text-gray-500'>
                          Values from: {pivotConfig.valueColumn.name} ({pivotConfig.aggregation}) •
                          Grouped by: {pivotConfig.anchorColumn.name}
                          {!hasExtractedFiles && (
                            <span className='ml-1 text-amber'>
                              — Upload files to see generated columns
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={onOpenPivotModal}
                      disabled={!hasExtractedFiles}
                      className='border-emerald bg-white hover:border-emerald text-emerald hover:bg-emerald/10 disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                      Edit Pivot
                    </Button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className='text-center py-8'>
              <Button
                variant='outline'
                onClick={onOpenPivotModal}
                disabled={!hasExtractedFiles}
                className='border-emerald hover:border-emerald bg-white text-emerald hover:bg-emerald/10 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                <Plus className='h-4 w-4 mr-2' />
                Configure Deduction Code Pivot
              </Button>
              {!hasExtractedFiles && (
                <p className='text-sm text-gray-500 mt-3'>
                  Upload and extract files first to configure pivot.
                </p>
              )}
            </div>
          )}
        </div>
      </>
    );
  },
);

const IssuesStepContent = memo(
  ({
    validationErrors,
    onErrorClick,
  }: {
    validationErrors: LocalValidationError[];
    onErrorClick: (error: LocalValidationError) => void;
  }) => {
    return (
      <>
        <div className='px-6 py-4 bg-amber border-b'>
          <div className='flex items-center gap-3'>
            <div className='p-2 bg-white/20 rounded-lg'>
              <AlertTriangle className='h-5 w-5 text-white' />
            </div>
            <div>
              <h2 className='text-lg font-semibold text-white'>Configuration Issues</h2>
              <p className='text-sm text-white/80'>
                {validationErrors.length} issue{validationErrors.length !== 1 ? 's' : ''} to resolve
                before saving.
              </p>
            </div>
          </div>
        </div>
        <div className='p-6'>
          {validationErrors.length === 0 ? (
            <div className='py-12 text-center'>
              <CheckCircle className='h-12 w-12 mx-auto text-emerald mb-4' />
              <div className='text-lg font-medium text-gray-600 mb-2'>No issues found!</div>
              <p className='text-sm text-gray-500'>
                Your configuration is valid and ready to save.
              </p>
            </div>
          ) : (
            <div className='space-y-2'>
              {validationErrors.map((error, idx) => {
                const isClickable =
                  error.targetField ||
                  error.anchorFieldIdx !== undefined ||
                  error.type === 'unmapped' ||
                  error.type === 'anchor';

                return (
                  <div
                    key={idx}
                    className={clsx(
                      'flex items-center justify-between gap-2 p-3 bg-amber/5 rounded-lg border border-amber/20 transition-colors',
                      isClickable && 'cursor-pointer hover:bg-amber/10 hover:border-amber/30',
                    )}
                    onClick={() => isClickable && onErrorClick(error)}
                  >
                    <div className='flex items-start gap-3'>
                      <AlertCircle className='h-5 w-5 text-amber mt-0.5 shrink-0' />
                      <div>
                        <span className='text-sm font-medium text-internal-brand-navy'>
                          {error.message}
                        </span>
                        {isClickable && (
                          <p className='text-xs text-gray-500 mt-0.5'>Click to go to this issue</p>
                        )}
                      </div>
                    </div>
                    {isClickable && <ChevronRight className='h-5 w-5 text-gray-400' />}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </>
    );
  },
);
